import type { DailyTone } from "@/features/daily/components/command/command-view-model";

/**
 * The one stat-tile contract used by every KPI row in Daily (dataviz): exactly three
 * lines — short label · value (auto-compact) · delta and status pill (or one short
 * fact when the tile has no comparison). The previous value, the full name and
 * everything else go into the tile's tooltip, so every tile in a row has the same
 * height whatever the data.
 */
export type DeltaDirection = "up" | "down" | "flat";

export type DeltaVm = {
    label: string;
    direction: DeltaDirection;
    /** Whether this movement is good news; `null` when neither (or unknown). Colours follow it. */
    good: boolean | null;
};

export type StatTileVm = {
    key: string;
    /** Short enough to fit on one line at 1440 px; the ellipsis is only a last resort. */
    label: string;
    /** The full name, first line of the tooltip. */
    fullLabel: string;
    /** Line 2, compact: «602,7k €». */
    value: string;
    /** The exact figure, read by screen readers and shown in the tooltip: «602.737,93 EUR». */
    valueExact: string;
    valueTone: "good" | DailyTone;
    /**
     * D142 (Executive only): a fixed extra line under the value — «Anterior: €632.5k» or the
     * tile's target («Objetivo», «SLA», «Máximo», «Principal»). `null` keeps the 3-line tile.
     */
    reference: string | null;
    /** Line 3 for tiles without a comparison: one short fact. Never the previous value. */
    meta: string | null;
    delta: DeltaVm | null;
    /** A worded status (e.g. «INCUMPLE»), shown as a compact pill on line 3. */
    flag: { label: string; tone: DailyTone } | null;
    /** Definition, objective, full values and reasons — never extra lines on the tile. */
    tooltip: string[];
    /** When set, the value is a link into the population behind it. */
    href: string | null;
    border: DailyTone;
};

const ARROW: Record<DeltaDirection, string> = { up: "▲", down: "▼", flat: "—" };
const SPOKEN: Record<DeltaDirection, string> = { up: "sube", down: "baja", flat: "sin cambio" };

export const statTileViewModel = {
    arrow: (direction: DeltaDirection): string => ARROW[direction],
    /** The word a screen reader hears for the arrow. */
    spoken: (direction: DeltaDirection): string => SPOKEN[direction],
    /** Colour = direction × whether up is good: green good news, red bad, neutral otherwise. */
    deltaTone: (delta: DeltaVm): "good" | "critical" | "neutral" =>
        delta.good === null || delta.direction === "flat" ? "neutral" : delta.good ? "good" : "critical",
    tile: (partial: Pick<StatTileVm, "key" | "label" | "value" | "valueExact"> & Partial<StatTileVm>): StatTileVm => ({
        fullLabel: partial.label,
        reference: null,
        valueTone: "neutral",
        meta: null,
        delta: null,
        flag: null,
        tooltip: [],
        href: null,
        border: "neutral",
        ...partial,
    }),
};
